import React, { useState, useEffect, useCallback } from 'react';
import { getProjects, getScheduleLogPMs, getPMScheduleSummary } from '../api';
import ScheduleLogTab from '../components/ScheduleLogTab';

const C = {
  slate900:'#0f172a', slate700:'#334155', slate500:'#64748b', slate400:'#94a3b8',
  slate200:'#e2e8f0', slate100:'#f1f5f9', slate50:'#f8fafc',
  blue600:'#2563eb', blue500:'#3b82f6', blue50:'#eff6ff',
  green600:'#16a34a', green50:'#f0fdf4',
  amber600:'#d97706', amber50:'#fffbeb',
  rose600:'#dc2626', rose50:'#fef2f2',
  indigo:'#6366f1', white:'#fff',
};

const fieldIcon = (field) => {
  switch (field) {
    case 'Start Date': case 'Finish Date': return '📅';
    case 'Duration': return '⏱';
    case 'Percent Complete': return '📊';
    case 'Status': return '🏷';
    case 'Responsible Party': return '👤';
    default: return '📝';
  }
};

const fieldColor = (field) => {
  switch (field) {
    case 'Start Date': return { bg: C.blue50, fg: C.blue600 };
    case 'Finish Date': return { bg: C.rose50, fg: C.rose600 };
    case 'Duration': return { bg: C.amber50, fg: C.amber600 };
    case 'Percent Complete': return { bg: C.green50, fg: C.green600 };
    case 'Status': return { bg: '#f3e8ff', fg: '#7c3aed' };
    case 'Responsible Party': return { bg: C.slate100, fg: C.slate700 };
    default: return { bg: C.slate100, fg: C.slate500 };
  }
};

const fmtDate = (d) => {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const fmtTimestamp = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return d; }
};

const isDateField = (field) => field === 'Start Date' || field === 'Finish Date';

/* ═══════════════════════════════════════════════════════════════
   PM OVERVIEW COMPONENT — cross-project view for a single PM
   ═══════════════════════════════════════════════════════════════ */
const PMOverview = ({ pmData, onOpenProject }) => {
  const [pmView, setPmView] = useState('summary'); // summary | timeline | slippage

  const card = { background: C.white, borderRadius: 16, border: `1px solid ${C.slate200}`, padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)' };
  const pill = (active) => ({
    padding: '8px 20px', borderRadius: 24, border: 'none',
    background: active ? C.indigo : 'transparent', color: active ? '#fff' : C.slate500,
    fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
    boxShadow: active ? '0 2px 10px rgba(99,102,241,.3)' : 'none',
  });

  if (!pmData) return null;

  return (
    <div>
      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ ...card, flex: 1, minWidth: 140, marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>Projects</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.indigo }}>{pmData.projects.length}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 140, marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>Total Changes</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.slate900 }}>{pmData.totalChanges}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 140, marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>Finish Slips</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: pmData.slippage.length > 0 ? C.rose600 : C.green600 }}>{pmData.slippage.length}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 140, marginBottom: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>Avg / Project</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.amber600 }}>
            {pmData.projects.length > 0 ? Math.round(pmData.totalChanges / pmData.projects.length) : 0}
          </div>
        </div>
      </div>

      {/* Field breakdown */}
      {pmData.byField.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {pmData.byField.map(f => {
            const fc = fieldColor(f.field_changed);
            return (
              <div key={f.field_changed} style={{
                padding: '14px 20px', borderRadius: 12, border: `1px solid ${C.slate200}`, backgroundColor: C.white, minWidth: 120,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate400, marginBottom: 4 }}>
                  {fieldIcon(f.field_changed)} {f.field_changed}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: fc.fg }}>{f.change_count}</div>
                <div style={{ fontSize: 11, color: C.slate400 }}>changes</div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.slate100, borderRadius: 28, padding: 4, width: 'fit-content' }}>
        {[{ k: 'summary', l: 'By Project' }, { k: 'timeline', l: 'Recent Activity' }, { k: 'slippage', l: 'Slippage' }].map(t => (
          <button key={t.k} style={pill(pmView === t.k)} onClick={() => setPmView(t.k)}>{t.l}</button>
        ))}
      </div>

      {/* No data */}
      {pmData.totalChanges === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>📋</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.slate500, marginBottom: 4 }}>No Schedule Changes Recorded</div>
          <div style={{ fontSize: 13, color: C.slate400 }}>This PM has {pmData.projects.length} project{pmData.projects.length !== 1 ? 's' : ''} but no schedule changes have been logged yet.</div>
        </div>
      )}

      {/* ═══ BY PROJECT VIEW ═══ */}
      {pmView === 'summary' && pmData.byProject.length > 0 && (
        <div style={card}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Changes by Project</h3>
          <div style={{ border: `1px solid ${C.slate200}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '80px 1fr 100px 110px 110px 80px',
              padding: '10px 16px', backgroundColor: C.slate50, borderBottom: `2px solid ${C.slate200}`,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate500, gap: 8,
            }}>
              <div>Number</div><div>Project</div><div style={{ textAlign: 'center' }}>Changes</div>
              <div style={{ textAlign: 'center' }}>First</div><div style={{ textAlign: 'center' }}>Latest</div><div></div>
            </div>
            {pmData.byProject.map((p, idx) => (
              <div key={p.project_id} style={{
                display: 'grid', gridTemplateColumns: '80px 1fr 100px 110px 110px 80px',
                padding: '12px 16px', borderBottom: idx < pmData.byProject.length - 1 ? `1px solid ${C.slate100}` : 'none',
                alignItems: 'center', gap: 8, fontSize: 13,
              }}>
                <div style={{ fontWeight: 600, color: C.blue600, fontSize: 12 }}>{p.project_number || '–'}</div>
                <div style={{ color: C.slate900, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.project_name}</div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 8,
                    backgroundColor: p.change_count > 20 ? C.rose50 : p.change_count > 10 ? C.amber50 : C.green50,
                    color: p.change_count > 20 ? C.rose600 : p.change_count > 10 ? C.amber600 : C.green600,
                  }}>{p.change_count}</span>
                </div>
                <div style={{ textAlign: 'center', fontSize: 11, color: C.slate400 }}>{fmtDate(p.first_change)}</div>
                <div style={{ textAlign: 'center', fontSize: 11, color: C.slate400 }}>{fmtDate(p.last_change)}</div>
                <div>
                  <button onClick={() => onOpenProject(p.project_id)} style={{
                    fontSize: 11, fontWeight: 600, color: C.blue600, background: C.blue50, border: 'none', borderRadius: 6,
                    padding: '4px 10px', cursor: 'pointer',
                  }}>Open</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ RECENT ACTIVITY VIEW ═══ */}
      {pmView === 'timeline' && pmData.recentChanges.length > 0 && (
        <div style={card}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Recent Activity (Last 100)</h3>
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            <div style={{ position: 'absolute', left: 8, top: 4, bottom: 4, width: 2, backgroundColor: C.slate200 }} />
            {pmData.recentChanges.map((log, idx) => {
              const fc = fieldColor(log.field_changed);
              const isDate = isDateField(log.field_changed);
              return (
                <div key={log.id} style={{ position: 'relative', paddingBottom: idx < pmData.recentChanges.length - 1 ? 16 : 0, paddingLeft: 20 }}>
                  <div style={{ position: 'absolute', left: -20, top: 4, width: 14, height: 14, borderRadius: '50%', backgroundColor: fc.bg, border: `2px solid ${fc.fg}` }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, backgroundColor: C.blue50, color: C.blue600 }}>
                          {log.project_name}{log.project_number ? ` (${log.project_number})` : ''}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.blue600 }}>{log.task_code || '–'}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: C.slate900 }}>{log.task_name}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, backgroundColor: fc.bg, color: fc.fg }}>{log.field_changed}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                        <span style={{ color: C.slate500, textDecoration: 'line-through' }}>{isDate ? fmtDate(log.old_value) : log.old_value || '(empty)'}</span>
                        <span style={{ color: C.slate400 }}>→</span>
                        <span style={{ color: C.slate900, fontWeight: 600 }}>{isDate ? fmtDate(log.new_value) : log.new_value || '(empty)'}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: C.slate400, whiteSpace: 'nowrap', marginTop: 2 }}>{fmtTimestamp(log.changed_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ SLIPPAGE VIEW ═══ */}
      {pmView === 'slippage' && (
        <div style={card}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: C.slate900 }}>Finish Date Slippage — All Projects</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: C.slate400 }}>Tasks where the finish date was pushed later across this PM's projects.</p>
          {pmData.slippage.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: C.slate400, fontSize: 14 }}>
              No finish date slippage recorded.
            </div>
          ) : (
            <div style={{ border: `1px solid ${C.slate200}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 70px 1fr 110px 110px 80px 100px',
                padding: '10px 16px', backgroundColor: C.slate50, borderBottom: `2px solid ${C.slate200}`,
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: C.slate500, gap: 8,
              }}>
                <div>Project</div><div>Code</div><div>Task</div>
                <div style={{ textAlign: 'center' }}>Previous</div><div style={{ textAlign: 'center' }}>New</div>
                <div style={{ textAlign: 'center' }}>Slipped</div><div style={{ textAlign: 'right' }}>When</div>
              </div>
              {pmData.slippage.map((s, idx) => {
                const oldDate = new Date(s.old_value);
                const newDate = new Date(s.new_value);
                const days = Math.round((newDate - oldDate) / (1000 * 60 * 60 * 24));
                return (
                  <div key={s.id || idx} style={{
                    display: 'grid', gridTemplateColumns: '1fr 70px 1fr 110px 110px 80px 100px',
                    padding: '10px 16px', borderBottom: idx < pmData.slippage.length - 1 ? `1px solid ${C.slate100}` : 'none',
                    alignItems: 'center', gap: 8, fontSize: 13,
                    backgroundColor: days > 14 ? '#fff5f5' : 'transparent',
                  }}>
                    <div style={{ fontSize: 12, color: C.slate700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.project_name}
                    </div>
                    <div style={{ fontWeight: 600, color: C.blue600 }}>{s.task_code || '–'}</div>
                    <div style={{ color: C.slate900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.task_name}</div>
                    <div style={{ textAlign: 'center', color: C.slate500 }}>{fmtDate(s.old_value)}</div>
                    <div style={{ textAlign: 'center', color: C.rose600, fontWeight: 500 }}>{fmtDate(s.new_value)}</div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                        backgroundColor: days > 14 ? C.rose50 : C.amber50,
                        color: days > 14 ? C.rose600 : C.amber600,
                      }}>+{days}d</span>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: C.slate400 }}>{fmtTimestamp(s.changed_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const ScheduleLogPage = ({ onBack, onOpenProject }) => {
  const [projects, setProjects] = useState([]);
  const [pms, setPMs] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [pmData, setPmData] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pmLoading, setPmLoading] = useState(false);
  const [mode, setMode] = useState('pm'); // 'pm' = PM overview, 'project' = single project view

  useEffect(() => {
    const fetch = async () => {
      try {
        const [projectData, pmData] = await Promise.all([
          getProjects(),
          getScheduleLogPMs(),
        ]);
        setProjects(projectData);
        setPMs(pmData);
        if (projectData.length > 0) setSelectedProjectId(projectData[0].id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handlePMSelect = useCallback(async (pmName) => {
    setSelectedPM(pmName);
    if (!pmName) { setPmData(null); return; }
    try {
      setPmLoading(true);
      const data = await getPMScheduleSummary(pmName);
      setPmData(data);
    } catch (e) { console.error(e); }
    finally { setPmLoading(false); }
  }, []);

  const modeBtn = (active) => ({
    padding: '10px 24px', borderRadius: 10, border: `1px solid ${active ? C.indigo : C.slate200}`,
    backgroundColor: active ? `${C.indigo}10` : C.white, color: active ? C.indigo : C.slate500,
    fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all .15s',
  });

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{ width: 44, height: 44, border: `3px solid ${C.slate100}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: C.slate500, fontSize: 14, fontWeight: 500 }}>Loading schedule log...</div>
    </div>
  );

  return (
    <div>
      {/* Mode Switcher */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
        padding: '16px 24px', borderRadius: 14, border: `1px solid ${C.slate200}`,
        backgroundColor: C.white, boxShadow: '0 1px 4px rgba(0,0,0,.03)',
      }}>
        <div style={{ display: 'flex', gap: 8, marginRight: 16 }}>
          <button style={modeBtn(mode === 'pm')} onClick={() => setMode('pm')}>
            👤 By PM
          </button>
          <button style={modeBtn(mode === 'project')} onClick={() => setMode('project')}>
            📁 By Project
          </button>
        </div>

        <div style={{ width: 1, height: 32, backgroundColor: C.slate200 }} />

        {mode === 'pm' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.slate500, whiteSpace: 'nowrap' }}>Person:</div>
            <select
              value={selectedPM}
              onChange={e => handlePMSelect(e.target.value)}
              style={{
                flex: 1, maxWidth: 300, padding: '10px 14px', fontSize: 14, fontWeight: 600,
                border: `1px solid ${C.slate200}`, borderRadius: 10, backgroundColor: C.white,
                color: C.slate900, cursor: 'pointer', outline: 'none',
              }}
            >
              <option value="">Select a person...</option>
              {pms.map(pm => (
                <option key={pm} value={pm}>{pm}</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.slate500, whiteSpace: 'nowrap' }}>Project:</div>
            <select
              value={selectedProjectId || ''}
              onChange={e => setSelectedProjectId(parseInt(e.target.value))}
              style={{
                flex: 1, maxWidth: 400, padding: '10px 14px', fontSize: 14, fontWeight: 600,
                border: `1px solid ${C.slate200}`, borderRadius: 10, backgroundColor: C.white,
                color: C.slate900, cursor: 'pointer', outline: 'none',
              }}
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_name} {p.project_number ? `(${p.project_number})` : ''}</option>
              ))}
            </select>
            {selectedProjectId && (
              <button
                onClick={() => onOpenProject(selectedProjectId)}
                style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${C.slate200}`, backgroundColor: C.white, color: C.blue600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Open Project
              </button>
            )}
          </div>
        )}
      </div>

      {/* PM Mode Content */}
      {mode === 'pm' && (
        <>
          {!selectedPM && (
            <div style={{
              textAlign: 'center', padding: '80px 20px', border: `1px solid ${C.slate200}`,
              borderRadius: 14, backgroundColor: C.white, color: C.slate400,
            }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>👤</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.slate500, marginBottom: 4 }}>Select a Team Member</div>
              <div style={{ fontSize: 13 }}>Choose a person above to view their schedule change history across all projects.</div>
            </div>
          )}
          {pmLoading && (
            <div style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, border: `3px solid ${C.slate100}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin .7s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ color: C.slate500, fontSize: 14, fontWeight: 500 }}>Loading PM data...</div>
            </div>
          )}
          {selectedPM && pmData && !pmLoading && (
            <PMOverview pmData={pmData} onOpenProject={onOpenProject} />
          )}
        </>
      )}

      {/* Project Mode Content */}
      {mode === 'project' && (
        <>
          {selectedProjectId ? (
            <ScheduleLogTab projectId={selectedProjectId} />
          ) : (
            <div style={{
              textAlign: 'center', padding: '80px 20px', border: `1px solid ${C.slate200}`,
              borderRadius: 14, backgroundColor: C.white, color: C.slate400,
            }}>
              <div style={{ fontSize: 36, marginBottom: 12, opacity: .5 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.slate500, marginBottom: 4 }}>Select a Project</div>
              <div style={{ fontSize: 13 }}>Choose a project above to view its schedule change history.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleLogPage;
